import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import ManualSelectionsBanner from '../components/ManualSelectionsBanner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, Clock, Phone, Video, MapPin, Plus, Search, Loader2,
  MoreVertical, Check, X, AlertCircle, User, Building2, Briefcase,
  ChevronRight, Filter, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700', icon: Clock },
  done: { label: 'Done', color: 'bg-green-100 text-green-700', icon: Check },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-600', icon: X },
  no_show: { label: 'No Show', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: Check },  // Legacy status
  unknown: { label: 'Unknown', color: 'bg-gray-100 text-gray-600', icon: AlertCircle }
};

const MODE_CONFIG = {
  phone: { label: 'Phone', icon: Phone, color: 'text-blue-600' },
  video: { label: 'Video', icon: Video, color: 'text-purple-600' },
  in_person: { label: 'In-Person', icon: MapPin, color: 'text-green-600' }
};

export default function InterviewsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  
  // Users list for recruiter filter
  const [users, setUsers] = useState([]);
  
  // Schedule Interview Modal
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    candidate_id: '',
    requirement_id: '',
    round_name: 'Interview',
    mode: 'phone',
    scheduled_at: '',
    location: '',
    meeting_link: '',
    client_contact_name: '',
    client_contact_phone: '',
    notes: ''
  });

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (e) {
      console.error('Error loading users:', e);
    }
  };

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const params = new URLSearchParams({ limit: '200' });
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterRecruiter && filterRecruiter !== 'all' && isAdmin) params.append('recruiter_id', filterRecruiter);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const res = await fetch(`${API_URL}/api/manual-selections/interviews?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        let filteredInterviews = data.interviews || [];
        
        // Client-side search filter
        if (search) {
          const searchLower = search.toLowerCase();
          filteredInterviews = filteredInterviews.filter(i => 
            i.candidate_name?.toLowerCase().includes(searchLower) ||
            i.client_name?.toLowerCase().includes(searchLower) ||
            i.position_title?.toLowerCase().includes(searchLower) ||
            i.round_name?.toLowerCase().includes(searchLower)
          );
        }
        
        setInterviews(filteredInterviews);
        setTotal(filteredInterviews.length);
      } else {
        toast.error('Failed to load interviews');
      }
    } catch (e) {
      console.error('Error loading interviews:', e);
      toast.error('Error loading interviews');
    }
    setLoading(false);
  }, [filterStatus, filterRecruiter, fromDate, toDate, search, isAdmin]);

  const loadCandidatesAndRequirements = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      
      // Load requirement candidates
      const reqCandRes = await fetch(`${API_URL}/api/requirements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reqCandRes.ok) {
        const data = await reqCandRes.json();
        setRequirements(data.requirements || []);
      }
      
      // Load candidates from all requirements
      const allCandidates = [];
      const reqs = await reqCandRes.json().catch(() => ({ requirements: [] }));
      
      // Get candidates from each requirement
      for (const req of (reqs.requirements || []).slice(0, 10)) {
        const candRes = await fetch(`${API_URL}/api/requirements/${req.id}/candidates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (candRes.ok) {
          const candData = await candRes.json();
          (candData.candidates || []).forEach(c => {
            allCandidates.push({
              ...c,
              requirement_id: req.id,
              requirement_title: req.position_title,
              client_name: req.client_name
            });
          });
        }
      }
      setCandidates(allCandidates);
    } catch (e) {
      console.error('Error loading candidates:', e);
    }
  };

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  useEffect(() => {
    loadCandidatesAndRequirements();
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const resetForm = () => {
    setFormData({
      candidate_id: '',
      requirement_id: '',
      round_name: 'Interview',
      mode: 'phone',
      scheduled_at: '',
      location: '',
      meeting_link: '',
      client_contact_name: '',
      client_contact_phone: '',
      notes: ''
    });
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.candidate_id) {
      toast.error('Please select a candidate');
      return;
    }
    if (!formData.scheduled_at) {
      toast.error('Please select date and time');
      return;
    }
    if (formData.mode === 'video' && !formData.meeting_link) {
      toast.error('Meeting link is required for video interviews');
      return;
    }
    if (formData.mode === 'in_person' && !formData.location) {
      toast.error('Location is required for in-person interviews');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('ats_token');
      
      // Convert datetime-local to ISO
      const scheduledAt = new Date(formData.scheduled_at).toISOString();
      
      const payload = {
        ...formData,
        scheduled_at: scheduledAt,
        requirement_id: formData.requirement_id || null
      };

      const res = await fetch(`${API_URL}/api/manual-selections/interviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Interview scheduled successfully! Confirmation email sent.');
        setScheduleModalOpen(false);
        resetForm();
        loadInterviews();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to schedule interview');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error scheduling interview');
    }
    setSaving(false);
  };

  const handleStatusChange = async (interviewId, newStatus) => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API_URL}/api/manual-selections/interviews/${interviewId}/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Interview marked as ${newStatus}`);
        loadInterviews();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update status');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error updating status');
    }
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return '-';
    try {
      const date = new Date(isoStr);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) + ' IST';
    } catch {
      return isoStr;
    }
  };

  const handleCandidateSelect = (candidateId) => {
    const candidate = candidates.find(c => c.id === candidateId);
    if (candidate) {
      setFormData(prev => ({
        ...prev,
        candidate_id: candidateId,
        requirement_id: candidate.requirement_id || ''
      }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Calendar className="w-7 h-7 text-slate-600" />
              Interviews
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Schedule and manage candidate interviews
            </p>
          </div>
          <Button onClick={() => { resetForm(); setScheduleModalOpen(true); }} data-testid="schedule-interview-btn">
            <Plus className="w-4 h-4 mr-2" />
            Schedule Interview
          </Button>
        </div>

        {/* Manual Selections Banner */}
        <ManualSelectionsBanner />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by candidate, client, position..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="interview-search"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]" data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).filter(([key]) => key !== 'unknown').map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Recruiter Filter - Admin Only */}
              {isAdmin && (
                <Select value={filterRecruiter} onValueChange={setFilterRecruiter}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-recruiter">
                    <SelectValue placeholder="All Recruiters" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Recruiters</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-[140px]"
                  placeholder="From"
                  data-testid="filter-from-date"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-[140px]"
                  placeholder="To"
                  data-testid="filter-to-date"
                />
              </div>
              
              <Button variant="outline" size="sm" onClick={loadInterviews} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              
              <Badge variant="secondary" className="text-sm">
                {total} {total === 1 ? 'Interview' : 'Interviews'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Interviews Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : interviews.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="font-medium">No interviews found</p>
              <p className="text-sm mt-1">Schedule your first interview to get started</p>
              <Button onClick={() => setScheduleModalOpen(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Interview
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Candidate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client / Position</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Round</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Mode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">When (IST)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {interviews.map(interview => {
                    const statusCfg = STATUS_CONFIG[interview.status] || STATUS_CONFIG.unknown;
                    const modeCfg = MODE_CONFIG[interview.mode] || MODE_CONFIG.phone;
                    const StatusIcon = statusCfg.icon;
                    const ModeIcon = modeCfg.icon;
                    const isScheduled = interview.status === 'scheduled';
                    const isDone = ['done', 'completed'].includes(interview.status);
                    
                    return (
                      <tr key={interview.id} className="hover:bg-slate-50" data-testid={`interview-row-${interview.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <div className="font-medium text-slate-800">{interview.candidate_name}</div>
                              {interview.candidate_email && (
                                <div className="text-xs text-slate-500">{interview.candidate_email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {interview.client_name ? (
                            <div>
                              <div className="text-sm font-medium text-slate-700">{interview.client_name}</div>
                              {interview.position_title && (
                                <div className="text-xs text-slate-500">{interview.position_title}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700">{interview.round_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`flex items-center gap-1.5 text-sm ${modeCfg.color}`}>
                            <ModeIcon className="w-4 h-4" />
                            {modeCfg.label}
                          </div>
                          {interview.mode === 'video' && interview.meeting_link && (
                            <a 
                              href={interview.meeting_link} 
                              target="_blank" 
                              rel="noreferrer"
                              className="text-xs text-blue-600 hover:underline mt-0.5 block truncate max-w-[120px]"
                            >
                              Join Link
                            </a>
                          )}
                          {interview.mode === 'in_person' && interview.location && (
                            <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[120px]">
                              {interview.location}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-700">
                            {formatDateTime(interview.scheduled_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${statusCfg.color} flex items-center gap-1 w-fit`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusCfg.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`interview-actions-${interview.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {isScheduled && (
                                <>
                                  <DropdownMenuItem onClick={() => handleStatusChange(interview.id, 'done')}>
                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                    Mark as Done
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleStatusChange(interview.id, 'cancelled')}>
                                    <X className="w-4 h-4 mr-2 text-slate-600" />
                                    Cancel
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleStatusChange(interview.id, 'no_show')}>
                                    <AlertCircle className="w-4 h-4 mr-2 text-red-600" />
                                    No Show
                                  </DropdownMenuItem>
                                </>
                              )}
                              {!isScheduled && !isDone && (
                                <DropdownMenuItem onClick={() => handleStatusChange(interview.id, 'scheduled')}>
                                  <Clock className="w-4 h-4 mr-2 text-blue-600" />
                                  Reschedule
                                </DropdownMenuItem>
                              )}
                              {isDone && (
                                <DropdownMenuItem onClick={() => handleStatusChange(interview.id, 'scheduled')}>
                                  <Clock className="w-4 h-4 mr-2 text-blue-600" />
                                  Schedule Another Round
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Schedule Interview Modal */}
        <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Schedule Interview
              </DialogTitle>
              <DialogDescription>
                Schedule a new interview with a candidate
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleScheduleSubmit} className="space-y-4">
              <div>
                <Label>Candidate *</Label>
                <Select 
                  value={formData.candidate_id} 
                  onValueChange={handleCandidateSelect}
                >
                  <SelectTrigger data-testid="select-candidate">
                    <SelectValue placeholder="Select a candidate" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name} {c.requirement_title ? `(${c.requirement_title})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Round Name</Label>
                  <Input
                    value={formData.round_name}
                    onChange={(e) => setFormData({...formData, round_name: e.target.value})}
                    placeholder="e.g., HR Round, Tech Round"
                  />
                </div>
                <div>
                  <Label>Mode *</Label>
                  <Select 
                    value={formData.mode} 
                    onValueChange={(v) => setFormData({...formData, mode: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">📞 Phone</SelectItem>
                      <SelectItem value="video">📹 Video</SelectItem>
                      <SelectItem value="in_person">📍 In-Person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Date & Time (IST) *</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})}
                  required
                  data-testid="interview-datetime"
                />
              </div>

              {formData.mode === 'video' && (
                <div>
                  <Label>Meeting Link *</Label>
                  <Input
                    value={formData.meeting_link}
                    onChange={(e) => setFormData({...formData, meeting_link: e.target.value})}
                    placeholder="https://zoom.us/j/... or Google Meet link"
                  />
                </div>
              )}

              {formData.mode === 'in_person' && (
                <div>
                  <Label>Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Office address or venue"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client Contact Name</Label>
                  <Input
                    value={formData.client_contact_name}
                    onChange={(e) => setFormData({...formData, client_contact_name: e.target.value})}
                    placeholder="Interviewer name"
                  />
                </div>
                <div>
                  <Label>Client Contact Phone</Label>
                  <Input
                    value={formData.client_contact_phone}
                    onChange={(e) => setFormData({...formData, client_contact_phone: e.target.value})}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special instructions or notes..."
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setScheduleModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="confirm-schedule-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Schedule Interview
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
